import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpDown, Clock, Folder, FolderPlus, Pencil, Plus, Search, ShieldCheck, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePresenceSummary } from "@/lib/collab";
import { AppShell } from "@/components/app/AppShell";
import { ProjectCard } from "@/components/app/ProjectCard";
import { NewPageDialog } from "@/components/app/NewPageDialog";
import { AuditDialog } from "@/components/app/AuditDialog";

const COLORS = ["#8b5cf6", "#64748b", "#ef4444", "#f59e0b", "#10b981", "#0ea5e9"];

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState("__all__");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [tag, setTag] = useState("");
  const [sort, setSort] = useState("recent");
  const [dialog, setDialog] = useState(false);
  const [audit, setAudit] = useState(false);
  const presence = usePresenceSummary();

  const load = useCallback(async () => {
    const [p, f] = await Promise.all([api.get("/projects", { params: { q, status, tag, sort, folder_id: folder === "__all__" ? undefined : folder } }), api.get("/folders")]);
    setProjects(p.data);
    setFolders(f.data);
  }, [q, status, tag, sort, folder]);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const counts = useMemo(() => projects.reduce((a, p) => ({ ...a, [p.folder_id || "none"]: (a[p.folder_id || "none"] || 0) + 1 }), {}), [projects]);
  const allTags = useMemo(() => [...new Set(projects.flatMap((p) => p.tags || []))], [projects]);
  const recent = useMemo(() => [...projects].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)).slice(0, 3), [projects]);

  const newFolder = async () => {
    const name = window.prompt("Nome da pasta");
    if (!name?.trim()) return;
    try { await api.post("/folders", { name, color: COLORS[folders.length % COLORS.length] }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const renameFolder = async (f) => {
    const name = window.prompt("Renomear pasta", f.name);
    if (!name?.trim()) return;
    await api.patch(`/folders/${f.id}`, { name, color: f.color }); load();
  };
  const deleteFolder = async (f) => {
    if (!window.confirm(`Excluir a pasta "${f.name}"? As páginas voltam para "Sem pasta".`)) return;
    await api.delete(`/folders/${f.id}`); if (folder === f.id) setFolder("__all__"); load();
  };
  const hasFilters = q || status || tag;

  return (
    <AppShell>
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <h1 data-testid="dashboard-title" className="font-display font-bold text-3xl tracking-tight text-[var(--ink)]">Meus projetos</h1>
          <p className="text-slate-500 mt-1 flex items-center gap-3">Importe, edite e publique suas páginas. <span className="rounded-full border border-slate-200 px-3 py-0.5 text-xs">Plano Agência · {projects.length}/ilimitado projetos</span></p>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="audit-origin-button" onClick={() => setAudit(true)} className="btn btn-ghost !rounded-xl" title="Verificar quais páginas ainda dependem de arquivos de outro site"><ShieldCheck size={16} /> Verificar origem</button>
          <button data-testid="new-folder-button" onClick={newFolder} className="btn btn-ghost !rounded-xl"><FolderPlus size={16} /> Nova pasta</button>
          <button data-testid="new-project-button" onClick={() => setDialog(true)} className="btn btn-primary !rounded-xl"><Plus size={16} /> Novo projeto</button>
        </div>
      </div>

      <div className="mt-8 grid lg:grid-cols-[240px_1fr] gap-8">
        <aside data-testid="folders-sidebar" className="lg:sticky lg:top-24 self-start">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-3">Pastas</div>
          <ul className="flex flex-col gap-1">
            <li><button data-testid="folder-all" onClick={() => setFolder("__all__")} className={`w-full flex items-center justify-between rounded-lg px-3 py-2 text-sm ${folder === "__all__" ? "bg-[var(--ink)] text-white" : "hover:bg-slate-50"}`}><span className="flex items-center gap-2"><Folder size={14} /> Todos</span><span className="font-mono text-xs opacity-70">{projects.length}</span></button></li>
            <li><button data-testid="folder-none" onClick={() => setFolder("none")} className={`w-full flex items-center justify-between rounded-lg px-3 py-2 text-sm ${folder === "none" ? "bg-[var(--ink)] text-white" : "hover:bg-slate-50"}`}><span className="flex items-center gap-2"><Folder size={14} /> Sem pasta</span><span className="font-mono text-xs opacity-70">{counts.none || 0}</span></button></li>
            <li className="my-2 border-t border-slate-100" />
            {folders.map((f) => (
              <li key={f.id} className="group flex items-center">
                <button data-testid={`folder-${f.id}`} onClick={() => setFolder(f.id)} className={`flex-1 flex items-center justify-between rounded-lg px-3 py-2 text-sm ${folder === f.id ? "bg-[var(--ink)] text-white" : "hover:bg-slate-50"}`}><span className="flex items-center gap-2 truncate"><Folder size={14} style={{ color: folder === f.id ? "#fff" : f.color }} fill={folder === f.id ? "transparent" : f.color} /> <span className="truncate">{f.name}</span></span><span className="font-mono text-xs opacity-70">{counts[f.id] || 0}</span></button>
                <button onClick={() => renameFolder(f)} className="opacity-0 group-hover:opacity-100 p-1.5 text-slate-400 hover:text-[var(--ink)]" title="Renomear"><Pencil size={12} /></button>
                <button onClick={() => deleteFolder(f)} className="opacity-0 group-hover:opacity-100 p-1.5 text-slate-400 hover:text-red-600" title="Excluir"><Trash2 size={12} /></button>
              </li>
            ))}
          </ul>
        </aside>

        <section>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex-1 min-w-[240px] h-10 rounded-xl border border-slate-200 flex items-center gap-2 px-3 text-sm focus-within:border-[var(--blue)]"><Search size={15} className="text-slate-400" /><input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar páginas por nome ou slug…" className="flex-1 outline-none bg-transparent" /></label>
            <div className="flex rounded-xl border border-slate-200 p-1 text-sm">
              {[["", "Todas"], ["published", "Publicadas"], ["draft", "Rascunhos"]].map(([v, l]) => <button key={v} data-testid={`filter-${v || "all"}`} onClick={() => setStatus(v)} className={`px-3 h-8 rounded-lg ${status === v ? "bg-[var(--ink)] text-white" : "text-slate-600 hover:bg-slate-50"}`}>{l}</button>)}
            </div>
            <select data-testid="sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className="h-10 rounded-xl border border-slate-200 px-3 text-sm bg-white"><option value="recent">Recentes</option><option value="oldest">Mais antigos</option><option value="name">Nome A–Z</option></select>
            {allTags.length > 0 && <select data-testid="tag-select" value={tag} onChange={(e) => setTag(e.target.value)} className="h-10 rounded-xl border border-slate-200 px-3 text-sm bg-white"><option value="">Tag</option>{allTags.map((t) => <option key={t}>{t}</option>)}</select>}
            {hasFilters && <button data-testid="clear-filters" onClick={() => { setQ(""); setStatus(""); setTag(""); }} className="text-xs text-slate-500 hover:text-[var(--ink)] flex items-center gap-1"><X size={12} /> limpar filtros</button>}
          </div>

          {recent.length > 0 && !hasFilters && (
            <div className="mt-6">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 flex items-center gap-2 mb-3"><Clock size={12} /> Continuar de onde parou</div>
              <div className="grid sm:grid-cols-3 gap-3">
                {recent.map((p) => <button key={p.id} data-testid={`recent-${p.id}`} onClick={() => nav(`/editor/${p.id}`)} className="text-left rounded-xl border border-slate-200 px-4 py-3 hover:border-[var(--blue)] transition-colors flex items-center gap-3"><span className={`h-2 w-2 rounded-full ${p.status === "published" ? "bg-emerald-500" : "bg-amber-400"}`} /><div className="min-w-0"><div className="font-medium text-sm truncate">{p.name}</div><div className="text-xs text-slate-500">Editado {timeAgo(p.updated_at)}</div></div></button>)}
              </div>
            </div>
          )}

          {projects.length === 0 ? (
            <div data-testid="empty-state" className="mt-10 rounded-3xl border border-dashed border-slate-300 p-16 text-center">
              <h3 className="font-display font-semibold text-xl">Nenhuma página por aqui</h3>
              <p className="text-slate-500 mt-2">Cole o HTML da sua IA e publique em minutos.</p>
              <button onClick={() => setDialog(true)} className="btn btn-primary mt-6"><Plus size={16} /> Criar primeira página</button>
            </div>
          ) : (
            <div data-testid="projects-grid" className="mt-6 grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {projects.map((p) => <ProjectCard key={p.id} p={p} folders={folders} onChange={load} presence={presence[p.id] || []} />)}
            </div>
          )}
        </section>
      </div>
      {audit && <AuditDialog onChanged={load} onClose={() => { setAudit(false); load(); }} />}
      {dialog && <NewPageDialog folderId={folder !== "__all__" && folder !== "none" ? folder : null} onClose={() => setDialog(false)} />}
      <span className="hidden"><ArrowUpDown /> {user.email}</span>
    </AppShell>
  );
}
